import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import type {
  AuthResponse,
  User,
  APIKey,
  CreateAPIKeyResponse,
  WalletAndQuota,
  UsageListResponse,
  UsageSummaryResponse,
  Team,
  ListMembersResponse,
  InviteMemberResponse,
  ListTransactionsResponse,
  RechargeResult,
  TeamQuota,
  AdminListTeamsResponse,
  AddTeamAdminResponse,
  ScienceLLM,
  Provider,
  ProviderModel,
  ScienceLLMProviderModel,
  RetryStatsResponse,
  AdminAttemptListResponse,
  AdminAttemptDetailResponse,
  AdminTeamRateLimitView,
  AdminTeamRateLimitListResponse,
} from "./types";

// =============================================================================
// Token management
// =============================================================================

const REFRESH_KEY = "st_refresh_token";

let accessToken: string | null = null;
let refreshPromise: Promise<string> | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function setRefreshToken(token: string | null) {
  if (token) {
    localStorage.setItem(REFRESH_KEY, token);
  } else {
    localStorage.removeItem(REFRESH_KEY);
  }
}

export function clearTokens() {
  accessToken = null;
  localStorage.removeItem(REFRESH_KEY);
}

// =============================================================================
// Axios instance
// =============================================================================

const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "",
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

// Request interceptor: attach Bearer token
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Response interceptor: auto-refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response &&
      (error.response.status === 403 || error.response.status === 401) &&
      error.response.data?.code === "team_archived"
    ) {
      clearTokens();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    // Don't retry refresh or login requests
    if (
      !error.response ||
      error.response.status !== 401 ||
      originalRequest._retried ||
      originalRequest.url?.includes("/auth/refresh") ||
      originalRequest.url?.includes("/auth/login")
    ) {
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearTokens();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    // Deduplicate concurrent refresh calls
    if (!refreshPromise) {
      refreshPromise = (async () => {
        try {
          const resp = await axios.post<AuthResponse>(
            `${import.meta.env.VITE_API_BASE_URL || ""}/api/v1/auth/refresh`,
            { refresh_token: refreshToken }
          );
          setAccessToken(resp.data.access_token);
          setRefreshToken(resp.data.refresh_token);
          return resp.data.access_token;
        } catch (refreshError) {
          clearTokens();
          window.location.href = "/login";
          throw refreshError;
        } finally {
          refreshPromise = null;
        }
      })();
    }

    try {
      const newToken = await refreshPromise;
      originalRequest._retried = true;
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }
);

// =============================================================================
// Auth API
// =============================================================================

export const authApi = {
  async login(
    email: string,
    password: string,
    captcha?: { ticket: string; randstr: string },
  ): Promise<AuthResponse> {
    const resp = await api.post<AuthResponse>("/api/v1/auth/login", {
      email,
      password,
      captcha_ticket: captcha?.ticket,
      captcha_randstr: captcha?.randstr,
    });
    return resp.data;
  },

  async getCaptchaConfig(): Promise<{ enabled: boolean; app_id: string }> {
    const resp = await api.get<{ enabled: boolean; app_id: string }>(
      "/api/v1/auth/captcha",
    );
    return resp.data;
  },

  async refresh(refreshToken: string): Promise<AuthResponse> {
    const resp = await api.post<AuthResponse>("/api/v1/auth/refresh", {
      refresh_token: refreshToken,
    });
    return resp.data;
  },

  async logout(): Promise<void> {
    try {
      await api.post("/api/v1/auth/logout");
    } catch {
      // best effort
    }
  },

  async changePassword(
    old_password: string,
    new_password: string
  ): Promise<{ message: string }> {
    const resp = await api.post<{ message: string }>(
      "/api/v1/auth/change-password",
      { old_password, new_password }
    );
    return resp.data;
  },

  async me(): Promise<{
    user_id: string;
    team_id: string;
    role: string;
    is_system_admin: boolean;
  }> {
    const resp = await api.get("/api/v1/auth/me");
    return resp.data;
  },
};

// =============================================================================
// Users API
// =============================================================================

export const usersApi = {
  async getMe(): Promise<User> {
    const resp = await api.get<User>("/api/v1/users/me");
    return resp.data;
  },

  async updateMe(input: { display_name?: string }): Promise<User> {
    const resp = await api.patch<User>("/api/v1/users/me", input);
    return resp.data;
  },

  async listApiKeys(): Promise<APIKey[]> {
    const resp = await api.get<{ api_keys: APIKey[] | null }>(
      "/api/v1/users/me/api-keys"
    );
    return resp.data.api_keys ?? [];
  },

  async createApiKey(
    name: string,
    expires_at?: string
  ): Promise<CreateAPIKeyResponse> {
    const resp = await api.post<CreateAPIKeyResponse>(
      "/api/v1/users/me/api-keys",
      {
        name,
        expires_at,
      }
    );
    return resp.data;
  },

  async revokeApiKey(id: string): Promise<void> {
    await api.delete(`/api/v1/users/me/api-keys/${id}`);
  },

  async revealApiKey(id: string): Promise<string> {
    const resp = await api.get<{ key: string }>(
      `/api/v1/users/me/api-keys/${id}/reveal`
    );
    return resp.data.key;
  },

  async getMyUsage(
    params: {
      limit?: number;
      offset?: number;
      model?: string;
      api_key_id?: string;
      request_id?: string;
      status?: string;
      after?: string;
      before?: string;
    } = {}
  ): Promise<UsageListResponse> {
    const resp = await api.get<UsageListResponse>(
      "/api/v1/users/me/usage",
      { params }
    );
    return { ...resp.data, records: resp.data.records ?? [] };
  },

  async getMyUsageSummary(
    params: {
      group_by?: "model" | "day" | "api_key";
      after?: string;
      before?: string;
    } = {}
  ): Promise<UsageSummaryResponse> {
    const resp = await api.get<UsageSummaryResponse>(
      "/api/v1/users/me/usage/summary",
      { params }
    );
    return { ...resp.data, items: resp.data.items ?? [] };
  },
};

// =============================================================================
// Teams API
// =============================================================================

export const teamsApi = {
  async create(name: string): Promise<Team> {
    const resp = await api.post<Team>("/api/v1/teams", { name });
    return resp.data;
  },

  async get(tid: string): Promise<Team> {
    const resp = await api.get<Team>(`/api/v1/teams/${tid}`);
    return resp.data;
  },

  async update(tid: string, input: { name?: string }): Promise<Team> {
    const resp = await api.patch<Team>(`/api/v1/teams/${tid}`, input);
    return resp.data;
  },

  async listMembers(
    tid: string,
    opts: { includeInactive?: boolean } = {}
  ): Promise<ListMembersResponse> {
    const resp = await api.get<ListMembersResponse>(
      `/api/v1/teams/${tid}/members`,
      { params: opts.includeInactive ? { include_inactive: "true" } : {} }
    );
    return { ...resp.data, members: resp.data.members ?? [] };
  },

  async inviteMember(
    tid: string,
    input: { email: string; role: "member" | "team_admin"; display_name?: string }
  ): Promise<InviteMemberResponse> {
    const resp = await api.post<InviteMemberResponse>(
      `/api/v1/teams/${tid}/members`,
      input
    );
    return resp.data;
  },

  async updateMemberRole(
    tid: string,
    uid: string,
    role: "member" | "team_admin"
  ): Promise<{ message: string }> {
    const resp = await api.patch<{ message: string }>(
      `/api/v1/teams/${tid}/members/${uid}`,
      { role }
    );
    return resp.data;
  },

  async setMemberStatus(
    tid: string,
    uid: string,
    status: "active" | "inactive"
  ): Promise<{ message: string }> {
    const resp = await api.patch<{ message: string }>(
      `/api/v1/teams/${tid}/members/${uid}`,
      { status }
    );
    return resp.data;
  },

  async removeMember(
    tid: string,
    uid: string
  ): Promise<{ message: string }> {
    const resp = await api.delete<{ message: string }>(
      `/api/v1/teams/${tid}/members/${uid}`
    );
    return resp.data;
  },

  async resetMemberPassword(
    tid: string,
    uid: string
  ): Promise<{ message: string; initial_password: string }> {
    const resp = await api.post<{ message: string; initial_password: string }>(
      `/api/v1/teams/${tid}/members/${uid}/reset-password`
    );
    return resp.data;
  },

  async getWallet(tid: string): Promise<WalletAndQuota> {
    const resp = await api.get<WalletAndQuota>(
      `/api/v1/teams/${tid}/wallet`
    );
    return resp.data;
  },

  async listTransactions(
    tid: string,
    params: { page?: number; page_size?: number; type?: string } = {}
  ): Promise<ListTransactionsResponse> {
    const resp = await api.get<ListTransactionsResponse>(
      `/api/v1/teams/${tid}/transactions`,
      { params }
    );
    return { ...resp.data, transactions: resp.data.transactions ?? [] };
  },

  async getUsage(
    tid: string,
    params: {
      limit?: number;
      offset?: number;
      model?: string;
      user_id?: string;
      api_key_id?: string;
      request_id?: string;
      status?: string;
      after?: string;
      before?: string;
    } = {}
  ): Promise<UsageListResponse> {
    const resp = await api.get<UsageListResponse>(
      `/api/v1/teams/${tid}/usage`,
      { params }
    );
    return { ...resp.data, records: resp.data.records ?? [] };
  },

  async getRetryStats(
    tid: string,
    params: {
      from?: string;
      to?: string;
    } = {}
  ): Promise<RetryStatsResponse> {
    const resp = await api.get<RetryStatsResponse>(
      `/api/v1/teams/${tid}/usage/retry_stats`,
      { params }
    );
    return resp.data;
  },

  async getUsageSummary(
    tid: string,
    params: {
      group_by?: "model" | "day" | "api_key";
      user_id?: string;
      after?: string;
      before?: string;
    } = {}
  ): Promise<UsageSummaryResponse> {
    const resp = await api.get<UsageSummaryResponse>(
      `/api/v1/teams/${tid}/usage/summary`,
      { params }
    );
    return { ...resp.data, items: resp.data.items ?? [] };
  },

  async archiveTeam(tid: string): Promise<import("./types").ArchiveResult> {
    const resp = await api.post<import("./types").ArchiveResult>(`/api/v1/teams/${tid}/archive`);
    return resp.data;
  },
};

// =============================================================================
// Models API (public — returns ScienceLLM products users can call)
// =============================================================================

export const modelsApi = {
  async list(): Promise<ScienceLLM[]> {
    const resp = await api.get<{ models: ScienceLLM[] | null }>("/api/v1/models");
    return resp.data.models ?? [];
  },
};

// =============================================================================
// Admin API (system_admin only)
// =============================================================================




// On update, omit api_key to leave the existing encrypted key in place;
// pass a non-empty string to re-encrypt and replace it.


// --- ScienceLLM (product-level model) admin types ---

export interface CreateScienceLLMInput {
  name: string;
  model_key: string;
  description?: string;
  protocol: string;
  context_window: number;
  tpm_limit?: number;
  rpm_limit?: number;
  input_st_per_ktok: number;
  output_st_per_ktok: number;
  cache_write_st_per_ktok?: number;
  cache_read_st_per_ktok?: number;
}

export interface UpdateScienceLLMInput {
  name?: string;
  description?: string;
  protocol?: string;
  context_window?: number;
  tpm_limit?: number | null;
  rpm_limit?: number | null;
  input_st_per_ktok?: number;
  output_st_per_ktok?: number;
  cache_write_st_per_ktok?: number;
  cache_read_st_per_ktok?: number;
}



export const adminApi = {

  // Providers
  async getProviders(): Promise<Provider[]> {
    const resp = await api.get<{ providers: Provider[] | null }>("/api/v1/admin/providers");
    return resp.data.providers ?? [];
  },

  async createProvider(data: Partial<Provider> & { api_key?: string; google_service_account_json?: string }): Promise<Provider> {
    const resp = await api.post<Provider>("/api/v1/admin/providers", data);
    return resp.data;
  },

  async updateProvider(id: string, data: Partial<Provider> & { api_key?: string; google_service_account_json?: string }): Promise<Provider> {
    const resp = await api.put<Provider>(`/api/v1/admin/providers/${id}`, data);
    return resp.data;
  },

  async deactivateProvider(id: string): Promise<{ message: string }> {
    const resp = await api.post<{ message: string }>(`/api/v1/admin/providers/${id}/deactivate`);
    return resp.data;
  },

  async revealProviderKey(id: string): Promise<string> {
    const resp = await api.get<{ api_key: string }>(`/api/v1/admin/providers/${id}/reveal-key`);
    return resp.data.api_key;
  },

  // Provider Models
  async getProviderModels(providerId: string): Promise<ProviderModel[]> {
    const resp = await api.get<{ provider_models: ProviderModel[] | null }>(`/api/v1/admin/providers/${providerId}/models`);
    return resp.data.provider_models ?? [];
  },
  async searchProviderModels(params: { q?: string; page?: number; page_size?: number }): Promise<{
    provider_models: ProviderModel[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const resp = await api.get(`/api/v1/admin/provider-models/search`, { params });
    return resp.data;
  },

  async createProviderModel(providerId: string, data: Partial<ProviderModel>): Promise<ProviderModel> {
    const resp = await api.post<ProviderModel>(`/api/v1/admin/providers/${providerId}/models`, data);
    return resp.data;
  },

  async updateProviderModel(providerId: string, modelId: string, data: Partial<ProviderModel>): Promise<ProviderModel> {
    const resp = await api.put<ProviderModel>(`/api/v1/admin/providers/${providerId}/models/${modelId}`, data);
    return resp.data;
  },

  async deactivateProviderModel(providerId: string, modelId: string): Promise<{ message: string }> {
    const resp = await api.post<{ message: string }>(`/api/v1/admin/providers/${providerId}/models/${modelId}/deactivate`);
    return resp.data;
  },

  // ScienceLLM Provider Models
  async getScienceLLMProviderModels(id: string): Promise<ScienceLLMProviderModel[]> {
    const resp = await api.get<{ provider_models: ScienceLLMProviderModel[] | null }>(`/api/v1/admin/science-llms/${id}/provider-models`);
    return resp.data.provider_models ?? [];
  },

  async setScienceLLMProviderModels(
    id: string,
    items: { provider_model_id: string; weight: number }[]
  ): Promise<ScienceLLMProviderModel[]> {
    const resp = await api.put<{ provider_models: ScienceLLMProviderModel[] | null }>(
      `/api/v1/admin/science-llms/${id}/provider-models`,
      { provider_models: items }
    );
    return resp.data.provider_models ?? [];
  },

  // Team management
  async listAllTeams(
    params: { page?: number; page_size?: number } = {}
  ): Promise<AdminListTeamsResponse> {
    const resp = await api.get<AdminListTeamsResponse>("/api/v1/admin/teams", {
      params,
    });
    return { ...resp.data, teams: resp.data.teams ?? [] };
  },

  async createTeam(input: { name: string }): Promise<Team> {
    const resp = await api.post<Team>("/api/v1/admin/teams", input);
    return resp.data;
  },

  async addTeamAdmin(
    tid: string,
    input: { email: string }
  ): Promise<AddTeamAdminResponse> {
    const resp = await api.post<AddTeamAdminResponse>(
      `/api/v1/admin/teams/${tid}/admins`,
      input
    );
    return resp.data;
  },

  async getTeamAllowedProviders(tid: string): Promise<Provider[]> {
    const resp = await api.get<{ providers: Provider[] | null }>(
      `/api/v1/admin/teams/${tid}/allowed-providers`
    );
    return resp.data.providers ?? [];
  },

  async setTeamAllowedProviders(tid: string, providerIds: string[]): Promise<{ message: string }> {
    const resp = await api.put<{ message: string }>(
      `/api/v1/admin/teams/${tid}/allowed-providers`,
      { provider_ids: providerIds }
    );
    return resp.data;
  },

  // Wallet & quota
  async recharge(
    tid: string,
    input: { amount: string | number; note?: string }
  ): Promise<RechargeResult> {
    const resp = await api.post<RechargeResult>(
      `/api/v1/admin/teams/${tid}/recharge`,
      input
    );
    return resp.data;
  },

  async updateQuota(
    tid: string,
    input: {
      max_members?: number | null;
    }
  ): Promise<TeamQuota> {
    const resp = await api.patch<TeamQuota>(
      `/api/v1/admin/teams/${tid}/quota`,
      input
    );
    return resp.data;
  },

  // Per-team-per-model rate limits (tri-state semantics on PUT body):
  //   - OMIT a key (leave undefined): preserve that dimension's current value
  //     (axios drops undefined keys from the JSON payload).
  //   - Pass null: clear the override for that dimension (falls back to default).
  //   - Pass a number (>= 0): set that dimension; 0 is a valid hard-block value.
  //   The backend rejects values above the ScienceLLM ceiling with 400.
  async listTeamRateLimits(
    tid: string
  ): Promise<AdminTeamRateLimitListResponse> {
    const resp = await api.get<AdminTeamRateLimitListResponse>(
      `/api/v1/admin/teams/${tid}/rate-limits`
    );
    return {
      rate_limits: resp.data.rate_limits ?? [],
      defaults: resp.data.defaults,
    };
  },

  async getTeamRateLimit(
    tid: string,
    slid: string
  ): Promise<AdminTeamRateLimitView> {
    const resp = await api.get<AdminTeamRateLimitView>(
      `/api/v1/admin/teams/${tid}/science-llms/${slid}/rate-limit`
    );
    return resp.data;
  },

  async setTeamRateLimit(
    tid: string,
    slid: string,
    body: { tpm_limit?: number | null; rpm_limit?: number | null }
  ): Promise<AdminTeamRateLimitView> {
    const resp = await api.put<AdminTeamRateLimitView>(
      `/api/v1/admin/teams/${tid}/science-llms/${slid}/rate-limit`,
      body
    );
    return resp.data;
  },

  async deleteTeamRateLimit(
    tid: string,
    slid: string
  ): Promise<{ message: string }> {
    const resp = await api.delete<{ message: string }>(
      `/api/v1/admin/teams/${tid}/science-llms/${slid}/rate-limit`
    );
    return resp.data;
  },

  // UpstreamModel management (real backend LLM endpoints with pricing)
  

  // ScienceLLM (product-level model) management
  async listScienceLLMs(): Promise<ScienceLLM[]> {
    const resp = await api.get<{ science_llms: ScienceLLM[] | null }>(
      "/api/v1/admin/science-llms"
    );
    return resp.data.science_llms ?? [];
  },

  async createScienceLLM(input: CreateScienceLLMInput): Promise<ScienceLLM> {
    const resp = await api.post<ScienceLLM>("/api/v1/admin/science-llms", input);
    return resp.data;
  },

  async updateScienceLLM(
    id: string,
    input: UpdateScienceLLMInput
  ): Promise<ScienceLLM> {
    const resp = await api.patch<ScienceLLM>(
      `/api/v1/admin/science-llms/${id}`,
      input
    );
    return resp.data;
  },

  async deactivateScienceLLM(id: string): Promise<{ message: string }> {
    const resp = await api.post<{ message: string }>(
      `/api/v1/admin/science-llms/${id}/deactivate`
    );
    return resp.data;
  },

  

  

  // Cross-team usage records — sys_admin only, full upstream details exposed.
  async listUsage(
    params: {
      team_id?: string;
      user_id?: string;
      api_key_id?: string;
      model?: string;
      request_id?: string;
      status?: string;
      after?: string;
      before?: string;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<UsageListResponse> {
    const resp = await api.get<UsageListResponse>("/api/v1/admin/usage", {
      params,
    });
    return { ...resp.data, records: resp.data.records ?? [] };
  },

  async listUsageAttempts(
    params: {
      from: string;
      to: string;
      team_id?: string;
      outcome?: string;
      provider?: string;
      upstream_status?: number;
      page_size?: number;
      offset?: number;
      cursor?: string;
    }
  ): Promise<AdminAttemptListResponse> {
    const resp = await api.get<AdminAttemptListResponse>(
      "/api/v1/admin/usage/attempts",
      { params }
    );
    return { ...resp.data, records: resp.data.records ?? [] };
  },

  async getAttemptChain(
    request_id: string
  ): Promise<AdminAttemptDetailResponse> {
    const resp = await api.get<AdminAttemptDetailResponse>(
      `/api/v1/admin/usage/attempts/${request_id}`
    );
    return { ...resp.data, records: resp.data.records ?? [] };
  },

  async getUsageSummary(
    params: {
      team_id?: string;
      user_id?: string;
      group_by?: "model" | "day" | "api_key";
      after?: string;
      before?: string;
    } = {}
  ): Promise<UsageSummaryResponse> {
    const resp = await api.get<UsageSummaryResponse>(
      "/api/v1/admin/usage/summary",
      { params }
    );
    return { ...resp.data, items: resp.data.items ?? [] };
  },
};

export default api;
